// App admin page (0051, feedback #56): create and manage purveyors and the
// people who manage them, instead of scripts/seed-purveyors.mjs and 0050's
// SQL runbook. Only shown to app admins (see App.jsx), but hiding the tab is
// a convenience -- every write here is refused by RLS or the admin RPCs for
// anyone else.
//
// Products, boxes and vendor/venue links are not edited here: boxes belong
// to a purveyor's own members (Purveyor Portal), and products are #58.
import { useEffect, useState } from "react";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { FONT_SIZE, SPACE, RADIUS } from "../lib/constants";
import {
  loadPurveyorDirectory,
  createPurveyor,
  updatePurveyor,
  deletePurveyor,
  loadPurveyorContacts,
  invitePurveyorContact,
  revokePurveyorInvite,
  removePurveyorMember,
} from "../lib/db";
import { PurveyorProfileForm, PurveyorLocationsEditor, PurveyorHoursEditor } from "../components/PurveyorProfileEditor";
import { emptyPurveyorProfile, editorStyles } from "../lib/purveyorEditing";

export function AdminPage() {
  const { colors } = useMealPlanner();
  const { card, soft, accent, danger, line } = colors;
  const isMobile = useIsMobile();
  const s = editorStyles(colors);

  const [purveyors, setPurveyors] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState(null); // a purveyor id, "new", or null
  const [filter, setFilter] = useState("");

  async function reload(selectAfter) {
    const directory = await loadPurveyorDirectory();
    setPurveyors(directory);
    if (selectAfter !== undefined) setSelectedId(selectAfter);
  }

  useEffect(() => {
    reload().catch((e) => {
      console.error("[AdminPage] load failed:", e);
      setLoadError(true);
    });
  }, []);

  if (loadError) return <p className="mp-sans" style={{ color: danger }}>Couldn't load purveyors — try reloading the page.</p>;
  if (purveyors === null) return <p className="mp-sans" style={{ color: soft }}>Loading…</p>;

  const selected = purveyors.find((p) => p.id === selectedId) || null;
  const q = filter.trim().toLowerCase();
  const listed = [...purveyors].filter((p) => !q || p.name.toLowerCase().includes(q)).sort((a, b) => a.name.localeCompare(b.name));
  const updateSelected = (patch) => setPurveyors((list) => list.map((p) => (p.id === selectedId ? { ...p, ...patch } : p)));

  const panel = { background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xl2) var(--space-xxl)" };

  return (
    <div className="mp-sans">
      <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "0 0 var(--space-xl2)", maxWidth: 640 }}>
        Admin only. Add purveyors to the directory, keep their details current, and invite the people who manage them.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "260px 1fr", gap: SPACE.x3, alignItems: "start" }}>
        <div style={{ ...panel, display: "flex", flexDirection: "column", gap: SPACE.md }}>
          <button className="mp-btn" onClick={() => setSelectedId("new")} style={s.primary}>+ New purveyor</button>
          <input style={s.input} placeholder="Filter by name" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            {listed.length === 0 && <p style={{ margin: 0, color: soft, fontSize: FONT_SIZE.base }}>No purveyors.</p>}
            {listed.map((p) => (
              <button
                key={p.id}
                className="mp-btn"
                onClick={() => setSelectedId(p.id)}
                style={{ textAlign: "left", background: p.id === selectedId ? colors.paper : "transparent", color: p.id === selectedId ? accent : colors.ink, fontWeight: p.id === selectedId ? 600 : 400, padding: "var(--space-sm) var(--space-md)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
              >
                {p.name || "(unnamed)"}
                <span style={{ display: "block", fontSize: FONT_SIZE.xs, color: soft, fontWeight: 400 }}>{p.purveyorTypes.join(", ")}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          {selectedId === "new" && (
            <div style={panel}>
              <h2 style={{ fontSize: FONT_SIZE.h3, margin: "0 0 var(--space-xl)" }}>New purveyor</h2>
              <PurveyorProfileForm
                initial={emptyPurveyorProfile()}
                submitLabel="Create purveyor"
                showVerified
                colors={colors}
                isMobile={isMobile}
                onCancel={() => setSelectedId(null)}
                onSubmit={async (profile) => {
                  const id = await createPurveyor(profile);
                  await reload(id);
                }}
              />
            </div>
          )}
          {selected && (
            <PurveyorAdminDetail
              key={selected.id}
              purveyor={selected}
              panel={panel}
              colors={colors}
              isMobile={isMobile}
              onProfileSaved={() => reload()}
              onLocationsChange={(updater) => updateSelected({ pickupLocations: updater(selected.pickupLocations) })}
              onHoursChange={(updater) => updateSelected({ hours: updater(selected.hours) })}
              onDeleted={() => reload(null)}
            />
          )}
          {!selected && selectedId !== "new" && <p style={{ color: soft, margin: 0 }}>Pick a purveyor to edit, or create a new one.</p>}
        </div>
      </div>
    </div>
  );
}

function PurveyorAdminDetail({ purveyor, panel, colors, isMobile, onProfileSaved, onLocationsChange, onHoursChange, onDeleted }) {
  const s = editorStyles(colors);
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const heading = (text) => <h3 style={{ fontSize: FONT_SIZE.lg, margin: "0 0 var(--space-lg)" }}>{text}</h3>;
  const ownData = [
    purveyor.products.length && `${purveyor.products.length} product${purveyor.products.length === 1 ? "" : "s"}`,
    purveyor.boxes.length && `${purveyor.boxes.length} box${purveyor.boxes.length === 1 ? "" : "es"}`,
  ].filter(Boolean);

  async function handleDelete() {
    setDeleting(true);
    setDeleteError("");
    try {
      await deletePurveyor(purveyor.id);
      await onDeleted();
    } catch (err) {
      setDeleteError(err.message || "Couldn't delete — try again.");
      setDeleting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
      <div style={panel}>
        <h2 style={{ fontSize: FONT_SIZE.h3, margin: "0 0 var(--space-xl)" }}>{purveyor.name}</h2>
        <PurveyorProfileForm
          initial={purveyor}
          showVerified
          colors={colors}
          isMobile={isMobile}
          onSubmit={async (profile) => {
            setSaved(false);
            await updatePurveyor(purveyor.id, profile, purveyor.purveyorTypes);
            await onProfileSaved();
            setSaved(true);
          }}
        />
        {saved && <p style={{ fontSize: FONT_SIZE.sm, color: colors.accent, margin: "var(--space-md) 0 0" }}>Saved.</p>}
      </div>

      <div style={panel}>
        {heading("Pickup locations")}
        <PurveyorLocationsEditor purveyorId={purveyor.id} locations={purveyor.pickupLocations} onChange={onLocationsChange} colors={colors} isMobile={isMobile} />
      </div>

      <div style={panel}>
        {heading("Hours")}
        <PurveyorHoursEditor purveyorId={purveyor.id} hours={purveyor.hours} locations={purveyor.pickupLocations} onChange={onHoursChange} colors={colors} isMobile={isMobile} />
      </div>

      <div style={panel}>
        {heading("People who manage it")}
        <PurveyorContacts purveyorId={purveyor.id} colors={colors} />
      </div>

      <div style={panel}>
        {heading("Delete purveyor")}
        {!confirmDelete ? (
          <button className="mp-btn" onClick={() => setConfirmDelete(true)} style={s.danger}>Delete {purveyor.name}…</button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md, fontSize: FONT_SIZE.base }}>
            <p style={{ margin: 0, color: colors.danger }}>
              This removes {purveyor.name} from the directory for everyone, along with its locations, hours
              {ownData.length > 0 ? `, ${ownData.join(" and ")}` : ""}, its managers' access, and every household's ★ tag. Inventory items that came from it are kept. This can't be undone.
            </p>
            <div style={{ display: "flex", gap: SPACE.md }}>
              <button className="mp-btn" disabled={deleting} onClick={handleDelete} style={{ ...s.primary, background: colors.danger }}>
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
              <button className="mp-btn" disabled={deleting} onClick={() => setConfirmDelete(false)} style={s.secondary}>Cancel</button>
            </div>
            {deleteError && <p style={s.error}>{deleteError}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function PurveyorContacts({ purveyorId, colors }) {
  const s = editorStyles(colors);
  const [contacts, setContacts] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");

  const refresh = () => loadPurveyorContacts(purveyorId).then(setContacts);

  useEffect(() => {
    let cancelled = false;
    loadPurveyorContacts(purveyorId)
      .then((rows) => !cancelled && setContacts(rows))
      .catch(() => !cancelled && setError("Couldn't load the people for this purveyor."));
    return () => {
      cancelled = true;
    };
  }, [purveyorId]);

  async function run(action) {
    setBusy(true);
    setError("");
    try {
      await action();
      await refresh();
    } catch (err) {
      setError(err.message || "That didn't work — try again.");
    } finally {
      setBusy(false);
    }
  }

  const invite = (e) => {
    e.preventDefault();
    const address = email.trim();
    setSentTo("");
    return run(async () => {
      await invitePurveyorContact(purveyorId, address);
      setEmail("");
      setSentTo(address.toLowerCase());
    });
  };

  if (contacts === null) return <p style={{ margin: 0, color: colors.soft, fontSize: FONT_SIZE.base }}>{error || "Loading…"}</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md, fontSize: FONT_SIZE.base }}>
      {contacts.length === 0 && <p style={{ margin: 0, color: colors.soft }}>Nobody yet.</p>}
      {contacts.map((c) => (
        <div key={c.inviteId || c.userId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.md, flexWrap: "wrap" }}>
          <span>
            {c.email}{" "}
            <span style={{ color: c.expired ? colors.danger : colors.soft, fontSize: FONT_SIZE.sm }}>
              {c.kind === "member" ? "· manager" : c.expired ? "· invite expired" : "· invited"}
            </span>
          </span>
          {c.kind === "member" ? (
            <button className="mp-btn" disabled={busy} onClick={() => run(() => removePurveyorMember(purveyorId, c.userId))} style={s.danger}>Remove</button>
          ) : (
            <button className="mp-btn" disabled={busy} onClick={() => run(() => revokePurveyorInvite(c.inviteId))} style={s.danger}>Revoke</button>
          )}
        </div>
      ))}
      <form onSubmit={invite} style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap", marginTop: SPACE.sm }}>
        <input style={{ ...s.input, flex: "1 1 220px", width: "auto" }} type="email" placeholder="Their email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <button type="submit" className="mp-btn" disabled={busy} style={s.primary}>Invite</button>
      </form>
      {sentTo && (
        <p style={{ margin: 0, color: colors.soft, fontSize: FONT_SIZE.sm }}>
          Invite created. No email is sent — let {sentTo} know to sign in at weeklykitchen.me with that address; the invite will be waiting under Purveyor Portal.
        </p>
      )}
      {error && <p style={s.error}>{error}</p>}
    </div>
  );
}
